/**
 * NL-014 price-evidence and quote-receipt provenance contract.
 *
 * This module validates only caller-supplied, serializable metadata. It does
 * not calculate prices, select sources, read a runtime clock, create or
 * approve commercial records, or perform I/O. Result and approver references
 * are hashes so callers do not need to place raw inputs or PII in a receipt.
 *
 * A ready human-approved receipt is provenance metadata only. It is not a
 * payment settlement record and does not authorize any payment action.
 */
import { MAX_DECIMAL_OUTPUT_SCALE } from "./decimal";

export const PRICE_EVIDENCE_RECEIPT_CONTRACT_VERSION = "nl014-price-evidence-receipt-v1";

export const PRICE_EVIDENCE_USAGE_RIGHTS = ["approved", "unapproved", "unknown"] as const;

export type PriceEvidenceUsageRights = (typeof PRICE_EVIDENCE_USAGE_RIGHTS)[number];

export const PRICE_EVIDENCE_RECEIPT_KINDS = ["estimate", "quote", "receipt"] as const;

export type PriceEvidenceReceiptKind = (typeof PRICE_EVIDENCE_RECEIPT_KINDS)[number];

export type PriceEvidenceReceiptClassification =
  | "planning-estimate"
  | "human-approved-quote"
  | "human-approved-receipt";

/**
 * Immutable provenance references for the caller-selected price source. This
 * contract requires identifiers, versions, and hashes but cannot itself prove
 * how a future registry or storage layer preserves them.
 */
export type PriceEvidenceSource = {
  readonly sourceId: string;
  readonly sourceVersion: string;
  readonly sourceHash: string;
  readonly usageRights: PriceEvidenceUsageRights;
  readonly evidenceDate: string;
  readonly refreshDeadline: string;
};

/**
 * A privacy-safe reference to the computed result. The canonical result stays
 * with the caller; this receipt retains only its hash, declared unit, and the
 * currency/decimal context needed to interpret that hash.
 */
export type PriceEvidenceResultReference = {
  readonly resultHash: string;
  readonly unit: string;
  readonly currency: string;
  readonly decimalScale: number;
};

/**
 * `approverHash` must be an opaque hash or pseudonymous reference, never a
 * person name, email address, or other raw personal information.
 */
export type HumanApprovalReference = {
  readonly approvalId: string;
  readonly approverHash: string;
  readonly approvalDate: string;
};

/**
 * The serializable receipt shape that a later caller-owned calculation or
 * review flow may construct. It contains no price calculation inputs.
 */
export type PriceEvidenceReceipt = {
  readonly kind: PriceEvidenceReceiptKind;
  readonly calculatorVersion: string;
  readonly formulaVersion: string;
  readonly packageRuleVersion: string;
  readonly configurationHash: string;
  /** Canonical UTC timestamp (`YYYY-MM-DDTHH:mm:ssZ`), supplied by the caller. */
  readonly calculationTimestamp: string;
  readonly source: PriceEvidenceSource;
  readonly result: PriceEvidenceResultReference;
  readonly humanApproval?: HumanApprovalReference;
};

export type PriceEvidenceReceiptValidationInput = {
  /** `unknown` keeps this safe at future serialized-data boundaries. */
  readonly receipt?: unknown;
  /** A caller-supplied UTC calendar date; this module never reads the clock. */
  readonly asOfDate: string;
};

export type PriceEvidenceReceiptBlocker =
  | "missing-price-evidence-receipt"
  | "invalid-as-of-date"
  | "unrecognized-price-evidence-receipt-field"
  | "invalid-receipt-kind"
  | "missing-calculator-version"
  | "missing-formula-version"
  | "missing-package-rule-version"
  | "missing-configuration-hash"
  | "invalid-calculation-timestamp"
  | "calculation-after-as-of-date"
  | "missing-price-source"
  | "unrecognized-price-evidence-source-field"
  | "missing-source-id"
  | "missing-source-version"
  | "missing-source-hash"
  | "invalid-evidence-date"
  | "invalid-source-refresh-deadline"
  | "unapproved-source-rights"
  | "expired-price-source"
  | "missing-result-reference"
  | "unrecognized-price-evidence-result-field"
  | "missing-result-hash"
  | "missing-result-unit"
  | "invalid-result-currency"
  | "invalid-result-decimal-scale"
  | "human-approval-required"
  | "unrecognized-human-approval-field"
  | "missing-human-approval-id"
  | "missing-human-approver-hash"
  | "invalid-human-approval-date"
  | "human-approval-after-as-of-date"
  | "estimate-cannot-have-human-approval";

export type PriceEvidenceReceiptValidationResult = {
  readonly contractVersion: typeof PRICE_EVIDENCE_RECEIPT_CONTRACT_VERSION;
  readonly readiness: "ready" | "blocked";
  readonly blockers: readonly PriceEvidenceReceiptBlocker[];
  readonly classification: PriceEvidenceReceiptClassification | null;
};

type UnknownRecord = Readonly<Record<string, unknown>>;

const ISO_CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_UTC_TIMESTAMP = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})Z$/;
const ISO_4217_CURRENCY = /^[A-Z]{3}$/;

const RECEIPT_FIELDS = [
  "kind",
  "calculatorVersion",
  "formulaVersion",
  "packageRuleVersion",
  "configurationHash",
  "calculationTimestamp",
  "source",
  "result",
  "humanApproval",
] as const;

const SOURCE_FIELDS = [
  "sourceId",
  "sourceVersion",
  "sourceHash",
  "usageRights",
  "evidenceDate",
  "refreshDeadline",
] as const;

const RESULT_REFERENCE_FIELDS = ["resultHash", "unit", "currency", "decimalScale"] as const;

const HUMAN_APPROVAL_FIELDS = ["approvalId", "approverHash", "approvalDate"] as const;

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function rejectUnrecognizedFields(
  record: UnknownRecord,
  allowedFields: readonly string[],
  blocker: PriceEvidenceReceiptBlocker,
  blockers: PriceEvidenceReceiptBlocker[],
): void {
  if (Object.keys(record).some((field) => !allowedFields.includes(field))) {
    blockers.push(blocker);
  }
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function isCalendarDate(value: string | null): value is string {
  if (!value) return false;

  const match = ISO_CALENDAR_DATE.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const candidate = new Date(Date.UTC(year, month - 1, day));

  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
}

function isCanonicalUtcTimestamp(value: string | null): value is string {
  if (!value) return false;

  const match = ISO_UTC_TIMESTAMP.exec(value);
  if (!match || !isCalendarDate(match[1])) return false;

  const hour = Number(match[2]);
  const minute = Number(match[3]);
  const second = Number(match[4]);

  return hour <= 23 && minute <= 59 && second <= 59;
}

function isKnownUsageRights(value: unknown): value is PriceEvidenceUsageRights {
  return typeof value === "string" && (PRICE_EVIDENCE_USAGE_RIGHTS as readonly string[]).includes(value);
}

function isReceiptKind(value: unknown): value is PriceEvidenceReceiptKind {
  return typeof value === "string" && (PRICE_EVIDENCE_RECEIPT_KINDS as readonly string[]).includes(value);
}

function isDecimalScale(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= MAX_DECIMAL_OUTPUT_SCALE
  );
}

function getClassification(kind: PriceEvidenceReceiptKind): PriceEvidenceReceiptClassification {
  if (kind === "estimate") return "planning-estimate";
  if (kind === "quote") return "human-approved-quote";

  return "human-approved-receipt";
}

function validateSource(
  value: unknown,
  asOfDate: string | null,
  blockers: PriceEvidenceReceiptBlocker[],
): void {
  const source = asRecord(value);
  if (!source) {
    blockers.push("missing-price-source");
    return;
  }

  rejectUnrecognizedFields(
    source,
    SOURCE_FIELDS,
    "unrecognized-price-evidence-source-field",
    blockers,
  );

  const evidenceDate = nonEmptyString(source.evidenceDate);
  const refreshDeadline = nonEmptyString(source.refreshDeadline);

  if (!nonEmptyString(source.sourceId)) blockers.push("missing-source-id");
  if (!nonEmptyString(source.sourceVersion)) blockers.push("missing-source-version");
  if (!nonEmptyString(source.sourceHash)) blockers.push("missing-source-hash");
  if (!isCalendarDate(evidenceDate)) blockers.push("invalid-evidence-date");
  if (
    !isCalendarDate(refreshDeadline) ||
    (isCalendarDate(evidenceDate) && isCalendarDate(refreshDeadline) && evidenceDate > refreshDeadline)
  ) {
    blockers.push("invalid-source-refresh-deadline");
  }
  if (!isKnownUsageRights(source.usageRights) || source.usageRights !== "approved") {
    blockers.push("unapproved-source-rights");
  }
  if (isCalendarDate(asOfDate) && isCalendarDate(refreshDeadline) && asOfDate > refreshDeadline) {
    blockers.push("expired-price-source");
  }
}

function validateResultReference(value: unknown, blockers: PriceEvidenceReceiptBlocker[]): void {
  const result = asRecord(value);
  if (!result) {
    blockers.push("missing-result-reference");
    return;
  }

  rejectUnrecognizedFields(
    result,
    RESULT_REFERENCE_FIELDS,
    "unrecognized-price-evidence-result-field",
    blockers,
  );

  if (!nonEmptyString(result.resultHash)) blockers.push("missing-result-hash");
  if (!nonEmptyString(result.unit)) blockers.push("missing-result-unit");
  if (!ISO_4217_CURRENCY.test(nonEmptyString(result.currency) ?? "")) {
    blockers.push("invalid-result-currency");
  }
  if (!isDecimalScale(result.decimalScale)) blockers.push("invalid-result-decimal-scale");
}

function validateHumanApproval(
  value: unknown,
  asOfDate: string | null,
  blockers: PriceEvidenceReceiptBlocker[],
): void {
  const approval = asRecord(value);
  if (!approval) {
    blockers.push("human-approval-required");
    return;
  }

  const approvalDate = nonEmptyString(approval.approvalDate);

  if (!nonEmptyString(approval.approvalId)) blockers.push("missing-human-approval-id");
  if (!nonEmptyString(approval.approverHash)) blockers.push("missing-human-approver-hash");
  if (!isCalendarDate(approvalDate)) blockers.push("invalid-human-approval-date");
  if (isCalendarDate(asOfDate) && isCalendarDate(approvalDate) && approvalDate > asOfDate) {
    blockers.push("human-approval-after-as-of-date");
  }
}

/**
 * Validates source provenance and quote/receipt status deterministically.
 * Missing, expired, or unapproved source metadata is always blocked. Quotes
 * and receipts are ready only with a caller-supplied human-approval reference;
 * estimates must not claim that approval.
 */
export function validatePriceEvidenceReceipt(
  input: PriceEvidenceReceiptValidationInput,
): PriceEvidenceReceiptValidationResult {
  const blockers: PriceEvidenceReceiptBlocker[] = [];
  const asOfDate = nonEmptyString(input.asOfDate);

  if (!isCalendarDate(asOfDate)) blockers.push("invalid-as-of-date");

  const receipt = asRecord(input.receipt);
  if (!receipt) {
    blockers.push("missing-price-evidence-receipt");

    return {
      contractVersion: PRICE_EVIDENCE_RECEIPT_CONTRACT_VERSION,
      readiness: "blocked",
      blockers,
      classification: null,
    };
  }

  rejectUnrecognizedFields(
    receipt,
    RECEIPT_FIELDS,
    "unrecognized-price-evidence-receipt-field",
    blockers,
  );

  const kind = isReceiptKind(receipt.kind) ? receipt.kind : null;
  const calculationTimestamp = nonEmptyString(receipt.calculationTimestamp);

  if (!kind) blockers.push("invalid-receipt-kind");
  if (!nonEmptyString(receipt.calculatorVersion)) blockers.push("missing-calculator-version");
  if (!nonEmptyString(receipt.formulaVersion)) blockers.push("missing-formula-version");
  if (!nonEmptyString(receipt.packageRuleVersion)) blockers.push("missing-package-rule-version");
  if (!nonEmptyString(receipt.configurationHash)) blockers.push("missing-configuration-hash");
  if (!isCanonicalUtcTimestamp(calculationTimestamp)) {
    blockers.push("invalid-calculation-timestamp");
  } else if (isCalendarDate(asOfDate) && calculationTimestamp.slice(0, 10) > asOfDate) {
    blockers.push("calculation-after-as-of-date");
  }

  validateSource(receipt.source, asOfDate, blockers);
  validateResultReference(receipt.result, blockers);

  const humanApproval = asRecord(receipt.humanApproval);
  if (humanApproval) {
    rejectUnrecognizedFields(
      humanApproval,
      HUMAN_APPROVAL_FIELDS,
      "unrecognized-human-approval-field",
      blockers,
    );
  }

  if (kind === "estimate") {
    if (receipt.humanApproval !== undefined) blockers.push("estimate-cannot-have-human-approval");
  } else if (kind === "quote" || kind === "receipt") {
    validateHumanApproval(receipt.humanApproval, asOfDate, blockers);
  }

  return {
    contractVersion: PRICE_EVIDENCE_RECEIPT_CONTRACT_VERSION,
    readiness: blockers.length === 0 ? "ready" : "blocked",
    blockers,
    classification: blockers.length === 0 && kind ? getClassification(kind) : null,
  };
}
